import React, { Component } from 'react';
import { Button } from 'react-bootstrap';
import FieldRegistry from './FieldRegistry';
import { evaluateConditions } from './conditionEvaluator';
import { evaluateRoleAccess } from './roleAccessEvaluator';
import { fromModel, toModel } from './dataTransformer';
import { validateForm } from './validationEngine';

export default class DynamicFormRenderer extends Component {
    constructor(props) {
        super(props);
        this.state = {
            formValues: {},
            errors: [],
            errorMessages: {},
            loading: false,
            conditionResults: {}
        };
    }

    componentDidMount() {
        const { formDefinition, initialData } = this.props;
        if (formDefinition && formDefinition.fields) {
            const formValues = initialData
                ? fromModel(initialData, formDefinition.fields)
                : this.getDefaultValues(formDefinition.fields);
            this.setState({ formValues }, () => {
                this.evaluateAllConditions();
            });
        }
    }

    componentDidUpdate(prevProps) {
        if (prevProps.initialData !== this.props.initialData && this.props.initialData && this.props.formDefinition) {
            const formValues = fromModel(this.props.initialData, this.props.formDefinition.fields);
            this.setState({ formValues }, () => {
                this.evaluateAllConditions();
            });
        }
    }

    getDefaultValues = (fields) => {
        const values = {};
        fields.forEach((field) => {
            if (field.type === 'section' || field.type === 'hidden') return;
            if (field.type === 'address') {
                values[field.fieldId + '_address1'] = '';
                values[field.fieldId + '_address2'] = '';
                values[field.fieldId + '_city'] = '';
                values[field.fieldId + '_state'] = '';
                values[field.fieldId + '_zip'] = '';
                values[field.fieldId + '_country'] = '';
                return;
            }
            if (field.type === 'multiselect' || field.type === 'objectIdRefMulti') {
                values[field.fieldId] = field.defaultValue || [];
            } else if (field.type === 'checkbox') {
                values[field.fieldId] = field.defaultValue || false;
            } else {
                values[field.fieldId] = field.defaultValue || '';
            }
        });
        return values;
    };

    evaluateAllConditions = () => {
        const { formDefinition } = this.props;
        if (!formDefinition || !formDefinition.fields) return;

        const conditionResults = {};
        formDefinition.fields.forEach((field) => {
            conditionResults[field.fieldId] = evaluateConditions(field, this.state.formValues);
        });
        this.setState({ conditionResults });
    };

    handleChange = (fieldId, value) => {
        this.setState(
            (prevState) => ({
                formValues: { ...prevState.formValues, [fieldId]: value },
                errors: prevState.errors.filter((e) => e !== fieldId)
            }),
            () => {
                this.evaluateAllConditions();
            }
        );
    };

    hasError = (fieldId) => {
        return this.state.errors.indexOf(fieldId) !== -1;
    };

    validate = () => {
        const { formDefinition } = this.props;
        if (!formDefinition || !formDefinition.fields) return true;

        const { errors, errorMessages } = validateForm(
            this.state.formValues,
            formDefinition.fields,
            this.state.conditionResults
        );
        this.setState({ errors, errorMessages });
        return errors.length === 0;
    };

    handleSubmit = () => {
        if (!this.validate()) {
            return;
        }

        const { formDefinition, onSubmit } = this.props;
        const modelData = toModel(this.state.formValues, formDefinition.fields);

        if (onSubmit) {
            this.setState({ loading: true });
            onSubmit(modelData);
        }
    };

    getFormValues = () => {
        const { formDefinition } = this.props;
        return toModel(this.state.formValues, formDefinition.fields);
    };

    renderField = (fieldDef) => {
        // Check role access
        const roleAccess = evaluateRoleAccess(fieldDef);
        if (roleAccess === 'hidden') {
            return null;
        }

        // Check conditions
        const condResult = this.state.conditionResults[fieldDef.fieldId] || { visible: true, disabled: false };
        if (!condResult.visible) {
            return null;
        }

        const isDisabled = roleAccess === 'disabled' || condResult.disabled;

        // Hidden type fields
        if (fieldDef.type === 'hidden') {
            return null;
        }

        // Section header
        if (fieldDef.type === 'section') {
            const SectionComponent = FieldRegistry.section;
            return (
                <div key={fieldDef.fieldId} className="col-12">
                    <SectionComponent fieldDef={fieldDef} />
                </div>
            );
        }

        // Address block renders its own columns
        if (fieldDef.type === 'address') {
            const AddressComponent = FieldRegistry.address;
            return (
                <div key={fieldDef.fieldId} className="col-12">
                    <AddressComponent
                        fieldDef={fieldDef}
                        formValues={this.state.formValues}
                        onChange={this.handleChange}
                        disabled={isDisabled}
                    />
                </div>
            );
        }

        const FieldComponent = FieldRegistry[fieldDef.type];
        if (!FieldComponent) {
            return null;
        }

        const colClass = 'col-12 col-md-' + (fieldDef.colSpan || 12);

        return (
            <div key={fieldDef.fieldId} className={colClass}>
                <FieldComponent
                    fieldDef={fieldDef}
                    value={this.state.formValues[fieldDef.fieldId]}
                    formValues={this.state.formValues}
                    onChange={this.handleChange}
                    hasError={this.hasError(fieldDef.fieldId)}
                    errorMessage={this.state.errorMessages[fieldDef.fieldId]}
                    disabled={isDisabled}
                />
            </div>
        );
    };

    renderFieldsBySection = () => {
        const { formDefinition } = this.props;
        if (!formDefinition) return null;

        const fields = formDefinition.fields || [];
        const sections = formDefinition.sections || [];

        if (sections.length === 0) {
            // No sections, render all fields in order
            const sortedFields = [...fields].sort((a, b) => (a.order || 0) - (b.order || 0));
            return sortedFields.map((field) => this.renderField(field));
        }

        // Render fields grouped by section
        const sortedSections = [...sections].sort((a, b) => (a.order || 0) - (b.order || 0));
        const result = [];

        // Render fields without a section first
        const unsectionedFields = fields
            .filter((f) => !f.sectionId)
            .sort((a, b) => (a.order || 0) - (b.order || 0));
        unsectionedFields.forEach((field) => {
            result.push(this.renderField(field));
        });

        // Render each section with its fields
        sortedSections.forEach((section) => {
            const sectionFields = fields
                .filter((f) => f.sectionId === section.sectionId)
                .sort((a, b) => (a.order || 0) - (b.order || 0));

            if (sectionFields.length > 0) {
                result.push(
                    <div key={'section-' + section.sectionId} className="col-12 mt-3 mb-2">
                        <h5 style={{ borderBottom: '1px solid rgba(0,0,0,.1)', paddingBottom: '5px' }}>
                            {section.label}
                        </h5>
                    </div>
                );
                sectionFields.forEach((field) => {
                    result.push(this.renderField(field));
                });
            }
        });

        return result;
    };

    render() {
        const { formDefinition, onClose, hideButtons } = this.props;
        const submitLabel = (formDefinition && formDefinition.submitLabel) || 'Save Changes';

        return (
            <div>
                <form onSubmit={(e) => e.preventDefault()}>
                    <div className="row form-row">
                        {this.renderFieldsBySection()}
                    </div>
                    {!hideButtons && (
                        <div className="row">
                            <div
                                className="col-12"
                                style={{
                                    paddingTop: '10px',
                                    borderTop: '1px solid rgba(0,0,0,.2'
                                }}
                            >
                                <Button
                                    style={{ float: 'right', marginLeft: '10px' }}
                                    variant="primary"
                                    onClick={this.handleSubmit}
                                    disabled={this.state.loading}
                                >
                                    {submitLabel}
                                </Button>
                                {onClose && (
                                    <Button style={{ float: 'right' }} variant="secondary" onClick={onClose}>
                                        Close
                                    </Button>
                                )}
                            </div>
                        </div>
                    )}
                </form>
            </div>
        );
    }
}
